import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useHardwareFirstMembershipTerms from '../../hooks/useHardwareFirstMembershipTerms';
import SimpleDismissableModal from '../modals/SimpleDismissableModal';
import styles from './HardwareFirstPlanModal.module.scss';
import GpsAvatarDog from '../../assets/images/hardware-first/gps-avatar-dog.webp';

const STEPS = [
  { labelKey: 'hardware_first_step_at_checkout', bodyKey: 'hardware_first_step_checkout_body' },
  { labelKey: 'hardware_first_step_at_activation', bodyKey: 'hardware_first_step_activation_body' },
  { labelKey: 'hardware_first_step_using_fi', bodyKey: 'hardware_first_step_using_fi_body' },
];

// the compact sheet's steps name the tracker in the first one
const COMPACT_STEPS = [
  { labelKey: 'hardware_first_step_at_checkout', bodyKey: 'pdp_v2_plan_step_checkout_body' },
  { labelKey: 'pdp_v2_plan_step_arrives_label', bodyKey: 'pdp_v2_plan_step_arrives_body' },
  { labelKey: 'pdp_v2_plan_step_plan_label', bodyKey: 'pdp_v2_plan_step_plan_body' },
];

const BADGE_KEYS: Record<number, string> = { 12: 'most_popular', 24: 'best_value' };

/** the term label per renewal length; a term the copy does not know is not rendered */
const TERM_KEYS: Record<number, string> = {
  6: 'hardware_first_plan_term_6m',
  12: 'hardware_first_plan_term_1y',
  24: 'hardware_first_plan_term_2y',
};

interface HardwareFirstPlanModalProps {
  trigger: JSX.Element;
  /** The solo product page's sheet: map header, three short steps, one plan table, named tracker. */
  compact?: { deviceName: string };
}

/** Explains the activation-time plan choice behind the plp's "Fi GPS Cellular Plan" card. */
export default function HardwareFirstPlanModal({ trigger, compact }: HardwareFirstPlanModalProps) {
  const { t } = useTranslation();
  const terms = useHardwareFirstMembershipTerms();
  const steps = compact ? COMPACT_STEPS : STEPS;

  return (
    <SimpleDismissableModal trigger={trigger} containerClassName={styles.modal} closeIconClassName={styles.closeIcon}>
      {(close) => (
        <div className={classNames(styles.container, { [styles.compact]: !!compact })}>
          <div className={styles.scroll}>
            {compact ? (
              <div className={styles.mapHead}>
                {/* ollie's pin, pinging and ambling up the street: tracking that is visibly live */}
                <div className={styles.pin} aria-hidden="true">
                  <span className={styles.pulse} />
                  <span className={classNames(styles.pulse, styles.pulseTwo)} />
                  <span className={styles.pinBody}>
                    <span className={styles.pinFace}>
                      <img src={GpsAvatarDog} alt="" width={180} height={180} />
                    </span>
                    <span className={styles.pinTail} />
                  </span>
                </div>
                <span className={styles.livePill}>{t('pdp_v2_plan_tracking_live')}</span>
              </div>
            ) : (
              <div className={styles.hero}>
                <h2 className={styles.heroTitle}>{t('hardware_first_plan_modal_title')}</h2>
              </div>
            )}
            <section className={styles.howItWorks}>
              {compact ? (
                <h2 className={styles.compactTitle}>{t('pdp_v2_plan_modal_title')}</h2>
              ) : (
                <div className={styles.eyebrow}>{t('hardware_first_how_it_works')}</div>
              )}
              <div className={styles.steps}>
                {steps.map((step, index) => (
                  <div className={styles.step} key={step.labelKey}>
                    <span className={styles.stepNumber}>{index + 1}</span>
                    <span className={styles.stepText}>
                      <span className={styles.stepLabel}>{t(step.labelKey)}</span>
                      <span className={styles.stepBody}>{t(step.bodyKey, { device: compact?.deviceName })}</span>
                    </span>
                  </div>
                ))}
              </div>
            </section>
            {compact ? (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('pdp_v2_plan_col_plan')}</th>
                    <th>{t('pdp_v2_plan_col_first_term')}</th>
                    <th>{t('pdp_v2_plan_col_then')}</th>
                  </tr>
                </thead>
                <tbody>
                  {terms.map((term) => {
                    const termKey = TERM_KEYS[term.renewalMonths];
                    const badgeKey = BADGE_KEYS[term.renewalMonths];
                    if (!termKey) {
                      return null;
                    }
                    return (
                      <tr key={term.renewalMonths}>
                        <th>
                          <span className={styles.tableTerm}>{t(termKey)}</span>
                          {badgeKey && <span className={styles.tableBadge}>{t(badgeKey)}</span>}
                        </th>
                        <td>
                          <span className={styles.tableMonthly}>
                            {t('pdp_v2_plan_per_month', { amount: term.monthly })}
                          </span>
                          <span className={styles.tableTotal}>{t('pdp_v2_plan_total', { amount: term.intro })}</span>
                        </td>
                        <td className={styles.tableThen}>{term.renewal}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <section className={styles.plans}>
                <p className={styles.plansIntro}>{t('hardware_first_plan_modal_intro')}</p>
                {terms.map((term) => {
                  const termKey = TERM_KEYS[term.renewalMonths];
                  if (!termKey) {
                    return null;
                  }
                  return (
                    <div className={styles.planCard} key={term.renewalMonths}>
                      <div className={styles.planTop}>
                        <span className={styles.planTerm}>{t(termKey)}</span>
                        <span className={styles.planPricing}>
                          <span className={styles.planPriceRow}>
                            <span className={styles.planPrice}>{term.intro}</span>
                            <span className={styles.planListPrice}>{term.renewal}</span>
                          </span>
                          <span className={styles.planMonthly}>
                            {t('hardware_first_plan_equal_to_per_month', { amount: term.monthly })}
                          </span>
                        </span>
                      </div>
                      <div className={styles.planDivider} />
                      <div className={styles.planRenewal}>
                        {t('hardware_first_plan_renews_at', { price: term.renewal, term: t(termKey) })}
                      </div>
                    </div>
                  );
                })}
              </section>
            )}
          </div>
          <div className={styles.ctaBar}>
            <button type="button" className={styles.cta} onClick={close}>
              {t('got_it')}
            </button>
          </div>
        </div>
      )}
    </SimpleDismissableModal>
  );
}
